// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useMemo, useRef } from 'react';
import { filterOptions } from '../../autosuggest/utils/utils';
import { generateTestIndexes } from '../../internal/components/options-list/utils/test-indexes';
import { useHighlightedOption, } from '../../internal/components/options-list/utils/use-highlight-option';
import { calculateTokenPosition } from './caret-controller';
import { generateTokenId } from './dom-utils';
import { isPinnedReferenceToken, isReferenceToken, isTriggerToken } from './type-guards';
function isMenuItemHighlightable(option) {
    return !!option && option.type !== 'parent';
}
function isMenuItemInteractive(option) {
    return !!option && !option.disabled && option.type !== 'parent';
}
/** Manages menu item filtering, highlighting, and selection for a trigger menu. */
export const useMenuItems = ({ menu, filterText, onSelectItem, }) => {
    const { items, getItemGroup, getItemParent } = useMemo(() => createItems(menu.options), [menu.options]);
    const filteredItems = useMemo(() => {
        var _a;
        const filteringType = (_a = menu.filteringType) !== null && _a !== void 0 ? _a : 'auto';
        const filtered = filteringType === 'auto' ? filterOptions(items, filterText) : [...items];
        generateTestIndexes(filtered, getItemParent);
        return filtered;
    }, [menu.filteringType, items, filterText, getItemParent]);
    const [highlightedOptionState, highlightedOptionHandlers] = useHighlightedOption({
        options: filteredItems,
        isHighlightable: isMenuItemHighlightable,
    });
    const selectHighlightedOptionWithKeyboard = () => {
        const { highlightedOption } = highlightedOptionState;
        if (!highlightedOption || !isMenuItemInteractive(highlightedOption)) {
            return false;
        }
        onSelectItem(highlightedOption);
        return true;
    };
    const highlightVisibleOptionWithMouse = (index) => {
        const item = filteredItems[index];
        if (item && isMenuItemHighlightable(item)) {
            highlightedOptionHandlers.setHighlightedIndexWithMouse(index);
        }
    };
    const selectVisibleOptionWithMouse = (index) => {
        const item = filteredItems[index];
        if (item && isMenuItemInteractive(item)) {
            onSelectItem(item);
        }
    };
    return [
        { ...highlightedOptionState, items: filteredItems, showAll: false, getItemGroup },
        {
            ...highlightedOptionHandlers,
            selectHighlightedOptionWithKeyboard,
            highlightVisibleOptionWithMouse,
            selectVisibleOptionWithMouse,
        },
    ];
};
function createItems(options) {
    const items = [];
    const itemToGroup = new WeakMap();
    const getItemParent = (item) => itemToGroup.get(item);
    const getItemGroup = (item) => { var _a; return (_a = getItemParent(item)) === null || _a === void 0 ? void 0 : _a.option; };
    for (const option of options) {
        if (isGroup(option)) {
            for (const item of flattenGroup(option)) {
                items.push(item);
            }
        }
        else {
            items.push({ ...option, option });
        }
    }
    function flattenGroup(group) {
        const { options, ...rest } = group;
        let hasOnlyDisabledChildren = true;
        const groupItem = { ...rest, type: 'parent', option: group };
        const items = [groupItem];
        for (const option of options) {
            if (!option.disabled) {
                hasOnlyDisabledChildren = false;
            }
            const childOption = {
                ...option,
                type: 'child',
                disabled: option.disabled || rest.disabled,
                option,
            };
            items.push(childOption);
            itemToGroup.set(childOption, groupItem);
        }
        items[0].disabled = items[0].disabled || hasOnlyDisabledChildren;
        return items;
    }
    return { items, getItemGroup, getItemParent };
}
function isGroup(optionOrGroup) {
    const key = 'options';
    return key in optionOrGroup;
}
/** Manages pagination and load-more behavior for menu items. */
export const useMenuLoadMore = ({ menu, statusType, onLoadItems, onLoadMoreItems, }) => {
    const lastFilteringText = useRef(null);
    const fireLoadMore = (firstPage, samePage, filteringText) => {
        var _a;
        if (filteringText !== undefined && filteringText !== lastFilteringText.current) {
            lastFilteringText.current = filteringText;
        }
        if (filteringText === undefined || lastFilteringText.current !== filteringText) {
            onLoadItems({
                menuId: menu.id,
                filteringText: (_a = lastFilteringText.current) !== null && _a !== void 0 ? _a : '',
                firstPage,
                samePage,
            });
        }
    };
    const fireLoadMoreOnScroll = () => {
        if (menu.options.length > 0 && statusType === 'pending') {
            if (onLoadMoreItems) {
                onLoadMoreItems();
            }
            else {
                fireLoadMore(false, false);
            }
        }
    };
    const fireLoadMoreOnRecoveryClick = () => fireLoadMore(false, true);
    const fireLoadMoreOnMenuOpen = () => { var _a; return fireLoadMore(true, false, (_a = lastFilteringText.current) !== null && _a !== void 0 ? _a : ''); };
    const fireLoadMoreOnInputChange = (filteringText) => fireLoadMore(true, false, filteringText);
    return { fireLoadMoreOnScroll, fireLoadMoreOnRecoveryClick, fireLoadMoreOnMenuOpen, fireLoadMoreOnInputChange };
};
/** Replaces a trigger token with a reference token (or pinned token) after menu selection. */
export function handleMenuSelection(tokens, selectedOption, menuId, isPinned, activeTrigger) {
    const newTokens = [...tokens];
    const triggerIndex = newTokens.findIndex(t => isTriggerToken(t) && t.id === activeTrigger.id);
    if (isPinned) {
        const pinnedToken = {
            type: 'reference',
            id: generateTokenId(),
            label: selectedOption.label || selectedOption.value || '',
            value: selectedOption.value || '',
            menuId,
            pinned: true,
        };
        newTokens.splice(triggerIndex, 1);
        let insertIndex = 0;
        while (insertIndex < newTokens.length && isPinnedReferenceToken(newTokens[insertIndex])) {
            insertIndex++;
        }
        newTokens.splice(insertIndex, 0, pinnedToken);
        const caretPos = calculateTokenPosition(newTokens, insertIndex);
        return { tokens: newTokens, caretPosition: caretPos, insertedToken: pinnedToken };
    }
    const referenceToken = {
        type: 'reference',
        id: generateTokenId(),
        label: selectedOption.label || selectedOption.value || '',
        value: selectedOption.value || '',
        menuId,
    };
    newTokens.splice(triggerIndex, 1, referenceToken);
    const insertedIndex = newTokens.findIndex(t => isReferenceToken(t) && t.id === referenceToken.id);
    const caretPos = calculateTokenPosition(newTokens, insertedIndex);
    return { tokens: newTokens, caretPosition: caretPos, insertedToken: referenceToken };
}
//# sourceMappingURL=menu-state.js.map